
import { Options, Vue } from 'vue-class-component';
import { RawMaterialClient } from '@/services/Services';
import EditModal from '../modals/editModal.vue';
import InfoModal from '@/components/modals/infoModal.vue';
import MaterialTable from '../components/materialTable.vue';
import * as OM from '@/Model';

@Options({
    components: {
        MaterialTable
    }
})
export default class RawMaterial extends Vue {

    tabs = [
        { type: OM.MaterialType.Apparel, label: "Apparel" },
        { type: OM.MaterialType.Footwear, label: "Footwear" },
        { type: OM.MaterialType.Bag, label: "Bag" },
        { type: OM.MaterialType.Fabric, label: "Fabric" },
    ];

    activeTab: OM.MaterialType = OM.MaterialType.Apparel;

    itemsByType: { [key: number]: OM.RawMaterialConfigurationVM[] } = {
        [OM.MaterialType.Apparel]: [],
        [OM.MaterialType.Footwear]: [],
        [OM.MaterialType.Bag]: [],
        [OM.MaterialType.Fabric]: [],
    };

    loaded: boolean = false;

    created() {
        this.init();
    }

    init() {
        // Four calls instead of one, but the same documents over the wire as today's single unfiltered
        // call — and the counts are then available for every tab immediately.
        Promise.all([
            RawMaterialClient.getAllConfigurations(OM.MaterialType.Apparel),
            RawMaterialClient.getAllConfigurations(OM.MaterialType.Footwear),
            RawMaterialClient.getAllConfigurations(OM.MaterialType.Bag),
            RawMaterialClient.getAllConfigurations(OM.MaterialType.Fabric),
        ])
        .then(xs => {
            this.itemsByType[OM.MaterialType.Apparel] = xs[0];
            this.itemsByType[OM.MaterialType.Footwear] = xs[1];
            this.itemsByType[OM.MaterialType.Bag] = xs[2];
            this.itemsByType[OM.MaterialType.Fabric] = xs[3];
            this.loaded = true;
        })
        .catch(y => {
            // One rejection out of these four calls left `loaded` false forever, and the page renders
            // NOTHING while that holds -- no tabs, no table, no message. Tell the admin what happened,
            // same as materialTable.vue's and editModal.vue's identical catches.
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Error", "Error"),
                subtitle: this.$localizationService.getLocalizedValue(
                    "Could not load the materials.",
                    "Could not load the materials."),
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => {
                    this.$opModal.closeLast();
                }
            })
        })
    }

    /**
     * The server rejects creating a Fabric material through this endpoint: its 21 agronomic fields are not
     * on this view-model, so it would be born with a zero yield and divide by it at estimation time.
     * Editing an existing one is fine — the entity is loaded from Mongo and only the base fields change.
     */
    get canCreate() {
        return this.activeTab != OM.MaterialType.Fabric;
    }

    editConfiguration(item: OM.RawMaterialConfigurationVM) {
        if (!item) {
            item = new OM.RawMaterialConfigurationVM();
            // Add inherits the tab: on the Footwear tab you create a footwear material. Until now everything
            // created here was born apparel and then vanished from the shoe picker, which filters by type.
            item.materialType = this.activeTab;
        }

        this.$opModal.show(EditModal, {
            model: item,
            callback: (updatedModel: OM.RawMaterialConfigurationVM) => {
                RawMaterialClient.editConfiguration(updatedModel)
                .then(x => {
                    this.init();
                    this.$opModal.closeLast();
                })
                .catch(y => {
                    this.$opModal.show(InfoModal, {
                        text: this.$localizationService.getLocalizedValue("Error", "Error"),
                        subtitle: y.Message,
                        deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                        denyCb: () => {
                            this.$opModal.closeLast();
                        }
                    })
                })
            }
        })
    }

}
